import clsx from 'clsx';

import {
  CheckCircleIcon,
  InformationCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

type AlertType = 'success' | 'info' | 'warning' | 'error';

export type AlertProps = {
  type?: AlertType;
  message?: React.ReactNode;
  children?: React.ReactNode;
  description?: string;
  inline?: boolean;
  interactive?: boolean;
  className?: string;
};

export function Alert({
  type = 'info',
  message,
  children,
  description,
  inline = false,
  interactive = false,
  className,
}: AlertProps) {
  const cssClass = clsx(
    'flex rounded py-3',
    {
      ['align-middle']: !inline && !description,
      ['px-4']: !inline,
      ['border']: !inline,
      ['border-succes-50 bg-success-40']: type === 'success' && !inline,
      ['border-neutral-60 bg-neutral-30']: type === 'info' && !inline,
      ['border-warning-50 bg-warning-40']: type === 'warning' && !inline,
      ['border-primary-50 bg-primary-40']: type === 'error' && !inline,
      ['text-success-60']: type === 'success',
      ['text-neutral-80']: type === 'info',
      ['text-warning-60']: type === 'warning',
      ['text-primary-60']: type === 'error',
    },
    className
  );

  const icons: Record<AlertType, React.ReactNode> = {
    success: (
      <CheckCircleIcon className={clsx(`text-success-50`, 'w-5', 'h-5')} />
    ),
    info: (
      <InformationCircleIcon
        className={clsx(`text-neutral-70`, 'w-5', 'h-5')}
      />
    ),
    warning: (
      <ExclamationTriangleIcon
        className={clsx(`text-warning-50`, 'w-5', 'h-5')}
      />
    ),
    error: (
      <ExclamationCircleIcon
        className={clsx(`text-primary-50`, 'w-5', 'h-5')}
      />
    ),
  };

  // Consider using <dialog> instead of <section>, might be more
  // semantically correct. But it depends on how the alert is used.
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dialog

  const centered = ['flex', 'justify-center', 'items-center'];

  return (
    <section className={cssClass} role={interactive ? 'alertdialog' : 'alert'}>
      <span
        className={clsx(centered, 'h-5', {
          'mr-2': inline || !description,
          'mr-5': !inline && description,
        })}
      >
        {/* eslint-disable-next-line security/detect-object-injection */}
        {icons[type] || null}
      </span>
      <div className={clsx({ [centered.join(' ')]: inline || !description })}>
        <p className="cap-sm">{message || children}</p>
        {!inline && description && (
          <p className="body-xs mt-2">{description}</p>
        )}
      </div>
    </section>
  );
}

export default Alert;
